import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { initAnalytics } from '@/utils/initAnalytics';
import App from './App';

// Brand stylesheet (variables + base + components + animations + motion).
// Shared by React AND the legacy vanilla bundle, so it lives at the
// React entry instead of being pulled in by main.js — that lets Vite
// keep the CSS in the eager entry chunk while main.js becomes a
// truly lazy module. Without this, the build would either lazy-load
// the CSS (causing a flash of unstyled React) or block first paint
// on the legacy CSS chunk.
import './css/main.css';

// SSO from bebold.au website.
//
// This runs inside bootstrap() BEFORE createRoot().render(), so by the
// time <AppProvider> mounts and calls supabase.auth.getUser(), the
// session written by setSession() below is already persisted in
// localStorage and getUser() picks it up. No gate logic runs before this.
//
// If the returning user already has `language_selected: true` in their
// Supabase user_progress row, AppShell's gate in src/App.jsx will skip
// LanguageSelector and route them straight to the consent check / home.
// Use the [SSO] and [AppContext] console traces in DevTools to verify
// the handoff landed and the user_progress row reads back correctly.
async function handleSSOHandoff() {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token')) {
    // Normal page load, no SSO handoff in progress — silent.
    return;
  }

  console.log('[SSO] URL hash detected, parsing tokens...');

  const params = new URLSearchParams(hash.replace('#', ''));
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');

  if (!access_token || !refresh_token) {
    console.warn('[SSO] Hash present but tokens missing', {
      hasAccessToken: !!access_token,
      hasRefreshToken: !!refresh_token,
    });
    return;
  }

  console.log('[SSO] Applying session via supabase.auth.setSession()...');
  try {
    const { data, error } = await supabase.auth.setSession({
      access_token,
      refresh_token,
    });

    if (error) {
      console.error('[SSO] setSession failed:', error.message);
      // Leave the hash in place so the tokens are still present for
      // debugging / manual retry. Do NOT replaceState on failure.
      return;
    }

    console.log('[SSO] Session applied successfully', {
      userId: data?.session?.user?.id,
      email: data?.session?.user?.email,
      expiresAt: data?.session?.expires_at,
    });

    // Clean the URL so the tokens are not visible or reused.
    window.history.replaceState(null, '', window.location.pathname);
  } catch (err) {
    console.error('[SSO] setSession threw:', err);
  }
}

// Apply the user's saved theme to <html> before React renders. This
// avoids a flash of the default Rose palette on returning users who
// picked Sage or Twilight (would otherwise re-paint when fetchProgress
// resolves). No-op for first-time users (theme defaults to 'rose').
function applyThemeFromCache() {
  try {
    const saved = localStorage.getItem('bb7');
    if (!saved) return;
    const data = JSON.parse(saved);
    const theme = data?.theme;
    if (theme === 'sage' || theme === 'twilight') {
      document.documentElement.dataset.theme = theme;
    }
  } catch { /* localStorage disabled / quota — fall through to default */ }
}

// Lazy-load the legacy vanilla bundle (src/main.js, ~111 KB gzipped)
// once the React app is interactive. main.js is a pure side-effect
// script: it attaches handlers to vanilla `.screen` divs in
// index.html (assessment, find-feel, exploration, diary, feedback)
// and exposes globals used by no React component. New users on
// fully-React paths never trigger any of those screens, so they
// pay zero for the legacy bundle. Existing users navigating to a
// vanilla flow only reach those screens via main.js's own goScreen()
// — which can't fire until main.js loads — so there's no missing-
// handler window. Falls back to a microtask schedule when
// requestIdleCallback isn't available (Safari < 17).
function deferLegacyVanilla() {
  const load = () => import('./main.js').catch((err) => {
    console.warn('[bootstrap] legacy vanilla bundle failed to load', err);
  });
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(load, { timeout: 4000 });
  } else {
    setTimeout(load, 0);
  }
}

async function bootstrap() {
  applyThemeFromCache();
  // Fire-and-forget — analytics must never block the first paint.
  // No-op when VITE_POSTHOG_KEY is not set, so dev builds without
  // the env var pay zero bytes (the posthog-js import is dynamic).
  initAnalytics();
  await handleSSOHandoff();

  const rootEl = document.getElementById('root');
  if (rootEl) {
    createRoot(rootEl).render(
      <React.StrictMode>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </React.StrictMode>
    );
  }

  // After React mounts, defer the legacy vanilla bundle.
  deferLegacyVanilla();
}

bootstrap();
